import type { FinancialImport, FinancialQuoteBilling, FinancialServiceMapping, FinancialShipmentRecord } from '@/types/Models/Financial'

// Working drafts are scoped to the API environment and authenticated staff member.
// This is deliberately not the shared accounting ledger; clearing browser data removes drafts.
export const financialWorkspaceDatabase = async (owner: string): Promise<IDBDatabase> => {
  if (!owner) throw new Error('Sign in to open your Financial workspace.')
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(`pacsort-financial-v1:${import.meta.env.VITE_APP_API_LOCATION}:${owner}`, 4)
    request.onupgradeneeded = (event): void => {
      if (event.oldVersion < 1) {
        request.result
          .createObjectStore('imports', { keyPath: 'id' })
          .createIndex('fileHash', 'fileHash', { unique: true })
        request.result.createObjectStore('originals')
      }
      if (event.oldVersion < 2) request.result.createObjectStore('mappings', { keyPath: 'id' })
      if (event.oldVersion < 3) request.result.createObjectStore('shipments', { keyPath: 'itemId' })
      if (event.oldVersion < 4) request.result.createObjectStore('quotes', { keyPath: 'itemId' })
    }
    request.onerror = (): void =>
      reject(new Error('Your workspace could not be opened. Allow browser storage and retry.'))
    request.onblocked = (): void => reject(new Error('Close other Financial tabs and retry.'))
    request.onsuccess = (): void => {
      // Another tab upgrading the workspace must not be blocked by this short-lived connection.
      request.result.onversionchange = (): void => request.result.close()
      resolve(request.result)
    }
  })
}

const withStore = async <T>(
  owner: string,
  stores: string[],
  mode: IDBTransactionMode,
  work: (transaction: IDBTransaction, fail: (message: string) => void) => (() => T) | void,
  failure: string,
): Promise<T> => {
  const db = await financialWorkspaceDatabase(owner)
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(stores, mode)
      let message = failure
      const fail = (reason: string): void => {
        message = reason
        transaction.abort()
      }
      const result = work(transaction, fail)
      transaction.oncomplete = (): void => resolve((result ? result() : undefined) as T)
      transaction.onerror = (event): void =>
        reject(
          new Error(
            (event.target as IDBRequest | null)?.error?.name === 'ConstraintError'
              ? 'This file is already imported. Reload Financial to open the existing draft.'
              : message,
          ),
        )
      transaction.onabort = (): void => reject(new Error(message))
    })
  } finally {
    db.close()
  }
}

export const listFinancialImports = async (owner: string): Promise<FinancialImport[]> => {
  const imports = await withStore<FinancialImport[]>(
    owner,
    ['imports'],
    'readonly',
    (transaction) => {
      const request = transaction.objectStore('imports').getAll()
      return () => request.result as FinancialImport[]
    },
    'Saved imports could not be read. Retry opening the workspace.',
  )
  return imports.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

// Saves one or more imports atomically. A revision mismatch on any of them saves none.
export const saveFinancialImports = async (owner: string, batches: FinancialImport[], original?: File): Promise<void> => {
  const next = batches.map((batch) => (batch.revision ?? 0) + 1)
  await withStore<void>(
    owner,
    ['imports', 'originals'],
    'readwrite',
    (transaction, fail) => {
      const store = transaction.objectStore('imports')
      let stopped = false
      batches.forEach((batch, index) => {
        const request = store.get(batch.id)
        request.onsuccess = (): void => {
          if (stopped) return
          const saved = request.result as FinancialImport | undefined
          if (saved && (saved.revision ?? 0) !== (batch.revision ?? 0)) {
            stopped = true
            fail('This draft changed in another tab. Reload Financial before continuing; your changes were not saved.')
            return
          }
          store.put({ ...JSON.parse(JSON.stringify(batch)), revision: next[index] })
          if (original && index === 0) transaction.objectStore('originals').put(original, batch.id)
        }
      })
    },
    'Could not save this draft. Check browser storage; your previous saved version is unchanged.',
  )
  batches.forEach((batch, index) => {
    batch.revision = next[index]
  })
}

export const saveFinancialImport = (owner: string, batch: FinancialImport, original?: File): Promise<void> => {
  return saveFinancialImports(owner, [batch], original)
}

export const deleteFinancialImport = (owner: string, id: string): Promise<void> => {
  return withStore<void>(
    owner,
    ['imports', 'originals'],
    'readwrite',
    (transaction, fail) => {
      const request = transaction.objectStore('imports').get(id)
      request.onsuccess = (): void => {
        const saved = request.result as FinancialImport | undefined
        if (saved?.exports.some((item) => item.kind !== 'review')) {
          fail('This import has billing history. Keep it and review a correction instead of deleting it.')
          return
        }
        transaction.objectStore('imports').delete(id)
        transaction.objectStore('originals').delete(id)
      }
    },
    'The draft could not be deleted. Retry; nothing was removed.',
  )
}

export const financialOriginal = async (owner: string, id: string): Promise<Blob> => {
  const original = await withStore<Blob | undefined>(
    owner,
    ['originals'],
    'readonly',
    (transaction) => {
      const request = transaction.objectStore('originals').get(id)
      return () => request.result as Blob | undefined
    },
    'The original file could not be read.',
  )
  if (!original) throw new Error('The original file is no longer available.')
  return original
}

export const listFinancialMappings = (owner: string): Promise<FinancialServiceMapping[]> => {
  return withStore<FinancialServiceMapping[]>(
    owner,
    ['mappings'],
    'readonly',
    (transaction) => {
      const request = transaction.objectStore('mappings').getAll()
      return () => request.result as FinancialServiceMapping[]
    },
    'Confirmed service mappings could not be read.',
  )
}

export const saveFinancialMapping = (owner: string, mapping: FinancialServiceMapping): Promise<void> => {
  return withStore<void>(
    owner,
    ['mappings'],
    'readwrite',
    (transaction) => {
      transaction.objectStore('mappings').put({ ...mapping })
    },
    'The service mapping could not be saved.',
  )
}

export const listFinancialShipments = (owner: string): Promise<FinancialShipmentRecord[]> => {
  return withStore<FinancialShipmentRecord[]>(
    owner,
    ['shipments'],
    'readonly',
    (transaction) => {
      const request = transaction.objectStore('shipments').getAll()
      // Shipments saved before 11 October called the booked customer price optimisedPrice.
      return () =>
        (request.result as (FinancialShipmentRecord & { optimisedPrice?: string | null })[]).map(
          ({ optimisedPrice, ...record }) => ({ ...record, bookedPrice: record.bookedPrice ?? optimisedPrice ?? null }),
        )
    },
    'Saved shipments could not be read. Retry opening the workspace.',
  )
}

export const saveFinancialShipments = (owner: string, records: FinancialShipmentRecord[]): Promise<void> => {
  return withStore<void>(
    owner,
    ['shipments'],
    'readwrite',
    (transaction) => {
      const store = transaction.objectStore('shipments')
      records.forEach((record) => store.put(JSON.parse(JSON.stringify(record))))
    },
    'The shipments could not be saved. Check browser storage and retry.',
  )
}

export const listFinancialQuotes = (owner: string): Promise<FinancialQuoteBilling[]> => withStore(
  owner, ['quotes'], 'readonly', (transaction) => {
    const request = transaction.objectStore('quotes').getAll()
    return () => request.result as FinancialQuoteBilling[]
  }, 'Saved quoted billings could not be read.',
)

// Record the quote and cost-row adjustments together. An import, booking update or billing in another tab
// invalidates the preview: neither a file nor a partial billing history may then be produced.
export const saveFinancialBilling = async (
  owner: string,
  expected: { imports: FinancialImport[]; quotes: FinancialQuoteBilling[]; shipments: FinancialShipmentRecord[] },
  batches: FinancialImport[],
  quotes: FinancialQuoteBilling[],
): Promise<void> => {
  const nextBatches = batches.map((batch) => ({ ...batch, revision: (batch.revision ?? 0) + 1 }))
  const nextQuotes = quotes.map((quote) => ({ ...quote, revision: quote.revision + 1 }))
  await withStore<void>(owner, ['imports', 'quotes', 'shipments'], 'readwrite', (transaction, fail) => {
    const requests = ['imports', 'quotes', 'shipments'].map((name) => transaction.objectStore(name).getAll())
    let complete = 0
    requests.forEach((request) => {
      request.onsuccess = (): void => {
        complete += 1
        if (complete !== requests.length) return
        const imports = requests[0].result as FinancialImport[]
        const savedQuotes = requests[1].result as FinancialQuoteBilling[]
        const shipments = requests[2].result as FinancialShipmentRecord[]
        const signature = (values: string[]): string => JSON.stringify(values.sort())
        const importsKey = (values: FinancialImport[]): string => signature(values.map((item) => `${item.id}:${item.revision ?? 0}`))
        const quotesKey = (values: FinancialQuoteBilling[]): string => signature(values.map((item) => `${item.itemId}:${item.revision}`))
        const shipmentsKey = (values: FinancialShipmentRecord[]): string => signature(values.map((item) =>
          JSON.stringify([item.itemId, item.updatedAt, item.billingId, item.bookedPrice ?? (item as FinancialShipmentRecord & { optimisedPrice?: string }).optimisedPrice ?? null])))
        if (importsKey(imports) !== importsKey(expected.imports) || quotesKey(savedQuotes) !== quotesKey(expected.quotes) ||
          shipmentsKey(shipments) !== shipmentsKey(expected.shipments)) {
          fail('Financial changed in another tab. Reload before exporting; nothing was recorded or downloaded.')
          return
        }
        nextBatches.forEach((batch) => transaction.objectStore('imports').put(JSON.parse(JSON.stringify(batch))))
        nextQuotes.forEach((quote) => transaction.objectStore('quotes').put(JSON.parse(JSON.stringify(quote))))
      }
    })
  }, 'Could not record billing. Check browser storage; nothing was recorded or downloaded.')
  batches.forEach((batch, index) => { batch.revision = nextBatches[index].revision })
  quotes.forEach((quote, index) => { quote.revision = nextQuotes[index].revision })
}

export const financialFileBytes = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (): void => resolve(reader.result as ArrayBuffer)
    reader.onerror = (): void => reject(new Error('The file could not be read.'))
    reader.readAsArrayBuffer(file)
  })
}

export const financialFileHash = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await financialFileBytes(file))
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('')
}
